import { ReactElement, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useMunicipalPropertyUsageList } from "@/domain/municipal-property/hooks/api/use-municipal-property-usage-list";
import { MUNICIPAL_PROPERTY_BASE_PATH } from "@/domain/municipal-property/routes/municipal-property-base-path";
import { MUNICIPAL_PROPERTY_REGISTER_LIST_PATH } from "@/domain/municipal-property/routes/municipal-property-register-list-path";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeWorkspaceUrl } from "@/domain/workspace/lib/make-workspace-url";
import { useVirtualizer } from "@tanstack/react-virtual";

const VIRTUALIZATION_THRESHOLD: number = 100;
const ROW_HEIGHT: number = 28;
const VIRTUAL_ITEM_OVERSCAN: number = 10;

export function MunicipalPropertyUsageSection({ propertyId }: { propertyId: string }): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { t: tCommon }: ReturnType<typeof useTranslation<"common">> = useTranslation("common");
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const usage: ReturnType<typeof useMunicipalPropertyUsageList> = useMunicipalPropertyUsageList(propertyId);
  const usageList = usage.data?.pages.flatMap((page) => page.nodeList) ?? [];
  const scrollElementRef = useRef<HTMLUListElement>(null);
  const isVirtualized: boolean = usageList.length > VIRTUALIZATION_THRESHOLD;
  const virtualizer = useVirtualizer({
    count: isVirtualized ? usageList.length : 0,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ROW_HEIGHT,
    getItemKey: (index) => usageList[index].registerId,
    overscan: VIRTUAL_ITEM_OVERSCAN,
  });
  const virtualItemList = isVirtualized ? virtualizer.getVirtualItems() : [];
  const visibleUsageList = isVirtualized ? virtualItemList.map(({ index }) => usageList[index]) : usageList;
  return (
    <section className="pt-4" aria-labelledby="municipal-property-usage-title">
      <h3 id="municipal-property-usage-title" className="text-sm font-medium">
        {t("edit.usage.title")}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground">{t("edit.usage.description")}</p>
      {usage.isPending ? (
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          {t("catalog.loading")}
        </p>
      ) : usage.isError && !usage.isFetchNextPageError ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {tCommon("errors.pageError")}
        </p>
      ) : usageList.length ? (
        <div className="mt-2 text-sm">
          <ul ref={scrollElementRef} className="max-h-60 overflow-auto">
            {isVirtualized && <li aria-hidden="true" style={{ height: virtualItemList[0]?.start ?? 0 }} />}
            {visibleUsageList.map((entry, index) => (
              <li
                key={entry.registerId}
                className="h-7"
                aria-setsize={isVirtualized ? (usage.hasNextPage ? -1 : usageList.length) : undefined}
                aria-posinset={isVirtualized ? virtualItemList[index].index + 1 : undefined}
              >
                <Link
                  className="text-primary underline-offset-4 hover:underline"
                  to={makeWorkspaceUrl({
                    workspace: currentWorkspace,
                    path: `${MUNICIPAL_PROPERTY_BASE_PATH}/${MUNICIPAL_PROPERTY_REGISTER_LIST_PATH}/${entry.registerId}`,
                  })}
                >
                  {entry.year}
                </Link>
              </li>
            ))}
            {isVirtualized && (
              <li
                aria-hidden="true"
                style={{
                  height: virtualizer.getTotalSize() - (virtualItemList.at(-1)?.end ?? 0),
                }}
              />
            )}
          </ul>
          {usage.isFetchNextPageError && (
            <p role="alert" className="mt-2 text-sm text-destructive">
              {tCommon("errors.pageError")}
            </p>
          )}
          {(usage.hasNextPage || usage.isFetchNextPageError) && (
            <Button
              variant="ghost"
              size="xs"
              disabled={usage.isFetchingNextPage}
              onClick={() => void usage.fetchNextPage()}
            >
              {t(usage.isFetchNextPageError ? "register.property.retry" : "catalog.loadMore")}
            </Button>
          )}
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">{t("edit.usage.empty")}</p>
      )}
    </section>
  );
}
